/**
 * Discount-triggered gift automation
 *
 * v1 scope (for VIP private code use case):
 * - Reads currently applied discount codes from cart/cart-drawer discount tags.
 * - For configured codes, auto-adds a configured gift variant (B) when qualifying
 *   products (A variants) are present in the cart.
 * - Gift quantity = total quantity of qualifying A variants (1:A -> 1:B),
 *   capped by maxGiftQuantity per mapping.
 * - Gift items are marked with line item properties:
 *   - _is_discount_gift: 'true'
 *   - _discount_code: '<CODE>'
 *
 * Dependencies:
 * - window.theme.discountGift.mappings configured in theme.liquid.
 * - cart-block-discount.liquid rendering discount tags (data-discount-tags / data-discount-code).
 */

(function () {
  if (!window || !window.document) return;

  const config = (window.theme && window.theme.discountGift) || {};
  if (!config.enabled || !Array.isArray(config.mappings) || config.mappings.length === 0) {
    return;
  }

  class DiscountGiftManager {
    constructor(globalConfig) {
      this.config = globalConfig || {};
      this.mappings = Array.isArray(this.config.mappings) ? this.config.mappings : [];
      this.debug = !!this.config.debug;
      this.isSyncing = false;
      this.syncTimeout = null;
      this.collectionProductCache = {};

      if (!this.mappings.length) return;

      this.log('Initialized with mappings:', this.mappings);
      this.bindEvents();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.scheduleSync());
      } else {
        this.scheduleSync();
      }
    }

    log() {
      if (!this.debug) return;
      const args = Array.prototype.slice.call(arguments);
      args.unshift('[DiscountGift]');
      console.log.apply(console, args);
    }

    bindEvents() {
      document.addEventListener('theme:cart:refresh', () => {
        this.log('Event: theme:cart:refresh');
        this.scheduleSync();
      });

      // Cart content (quantities, items) changed
      document.addEventListener('theme:cart:change', () => {
        this.log('Event: theme:cart:change');
        this.scheduleSync();
      });

      document.addEventListener('theme:cart-drawer:open', () => {
        this.log('Event: theme:cart-drawer:open');
        this.scheduleSync();
      });

      document.addEventListener('theme:product:added', () => {
        this.log('Event: theme:product:added');
        this.scheduleSync();
      });

      this.observeDiscountTags();
    }

    scheduleSync() {
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout);
      }
      this.syncTimeout = setTimeout(() => this.syncFromDomAndCart(), 200);
    }

    observeDiscountTags() {
      try {
        if (!window.MutationObserver) return;

        const root =
          document.querySelector('[data-discount-wrapper]') ||
          document.querySelector('[data-cart-page]') ||
          document.querySelector('cart-drawer') ||
          document.body;

        if (!root) return;

        const observer = new MutationObserver((mutations) => {
          let shouldSync = false;

          mutations.forEach((mutation) => {
            if (shouldSync) return;

            const target = mutation.target;
            if (
              target &&
              target.getAttribute &&
              (target.hasAttribute('data-discount-tags') || target.hasAttribute('data-discount-wrapper'))
            ) {
              shouldSync = true;
              return;
            }

            (mutation.addedNodes || []).forEach((node) => {
              if (shouldSync) return;
              if (
                node &&
                node.nodeType === 1 &&
                node.getAttribute &&
                (node.hasAttribute('data-discount-tags') || node.hasAttribute('data-discount-wrapper'))
              ) {
                shouldSync = true;
              }
            });
          });

          if (shouldSync) {
            this.log('Detected discount tag DOM change, scheduling sync');
            this.scheduleSync();
          }
        });

        observer.observe(root, { childList: true, subtree: true });
        this.discountObserver = observer;
        this.log('MutationObserver attached for discount tags');
      } catch (e) {
        this.log('Error setting up discount tag observer:', e);
      }
    }

    getActiveCodesFromDom() {
      const codeSet = new Set();

      try {
        const tagRoots = document.querySelectorAll('[data-discount-tags]');
        tagRoots.forEach((root) => {
          const tags = root.querySelectorAll('[data-discount-code]');
          tags.forEach((el) => {
            const code = (el.getAttribute('data-discount-code') || '').trim();
            if (code) {
              codeSet.add(code.toUpperCase());
            }
          });
        });
      } catch (e) {
        this.log('Error reading active codes from DOM:', e);
      }

      return Array.from(codeSet);
    }

    async syncFromDomAndCart() {
      if (this.isSyncing) {
        this.log('Sync already in progress, skipping.');
        return;
      }

      this.isSyncing = true;

      try {
        const cart = await this.fetchCart();

        if (!cart) {
          this.log('No cart data available, aborting sync.');
          return;
        }

        const activeCodes = await this.getActiveCodes(cart);
        const activeMappings = this.getActiveMappingsForCodes(activeCodes);

        await this.syncGiftsWithCart(cart, activeMappings);
      } catch (e) {
        this.log('Error during sync:', e);
      } finally {
        this.isSyncing = false;
      }
    }

    async getActiveCodes(cart) {
      // 0. Prefer cart JSON if it exposes discount codes
      try {
        if (cart && Array.isArray(cart.discount_codes) && cart.discount_codes.length) {
          const codesFromCart = cart.discount_codes
            .map((dc) => (dc && dc.code ? String(dc.code).trim() : ''))
            .filter((c) => c)
            .map((c) => c.toUpperCase());

          if (codesFromCart.length) {
            this.log('Active discount codes from cart JSON:', codesFromCart);
            return codesFromCart;
          }
        }
      } catch (e) {
        this.log('Error reading discount_codes from cart JSON:', e);
      }

      // 1. Try DOM-based detection first (fast, used when customer applied code via cart UI)
      const domCodes = this.getActiveCodesFromDom();
      if (domCodes.length) {
        this.log('Active discount codes from DOM:', domCodes);
        return domCodes;
      }

      // 2. Fallback: fetch /cart HTML and infer codes
      // Only bother if there is some discount applied at cart level
      const hasAnyDiscount =
        cart &&
        ((typeof cart.total_discount === 'number' && cart.total_discount > 0) ||
          (Array.isArray(cart.cart_level_discount_applications) &&
            cart.cart_level_discount_applications.length > 0));

      if (!hasAnyDiscount) {
        this.log('No cart-level discount detected in cart JSON; skipping /cart HTML scan.');
        return [];
      }

      try {
        const res = await fetch('/cart', { credentials: 'same-origin' });
        if (!res.ok) {
          this.log('Failed to fetch /cart for discount codes:', res.status);
          return [];
        }

        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const codeSet = new Set();

        // 2a. If theme renders discount tags with data-discount-code, use those first
        const tagRoots = doc.querySelectorAll('[data-discount-tags]');
        tagRoots.forEach((root) => {
          const tags = root.querySelectorAll('[data-discount-code]');
          tags.forEach((el) => {
            const code = (el.getAttribute('data-discount-code') || '').trim();
            if (code) codeSet.add(code.toUpperCase());
          });
        });

        // 2b. If no explicit tags, attempt string match using known mapping codes
        if (!codeSet.size && Array.isArray(this.mappings)) {
          const upperHtml = html.toUpperCase();

          this.mappings.forEach((mapping) => {
            if (!mapping || !Array.isArray(mapping.codes)) return;

            mapping.codes.forEach((raw) => {
              if (!raw) return;
              const normalized = String(raw).trim().toUpperCase();
              if (!normalized) return;
              if (upperHtml.indexOf(normalized) !== -1) {
                codeSet.add(normalized);
              }
            });
          });
        }

        const codes = Array.from(codeSet);
        this.log('Active discount codes from /cart HTML:', codes);
        return codes;
      } catch (e) {
        this.log('Error fetching /cart for discount codes:', e);
        return [];
      }
    }

    getActiveMappingsForCodes(activeCodes) {
      if (!Array.isArray(activeCodes) || !activeCodes.length) return [];

      const activeCodeSet = new Set(activeCodes.map((c) => c.toUpperCase()));
      const results = [];

      this.mappings.forEach((mapping) => {
        if (!mapping || !Array.isArray(mapping.codes)) return;

        for (let i = 0; i < mapping.codes.length; i += 1) {
          const raw = mapping.codes[i];
          if (!raw) continue;
          const normalized = String(raw).trim().toUpperCase();
          if (normalized && activeCodeSet.has(normalized)) {
            results.push({ mapping, code: normalized });
            break;
          }
        }
      });

      this.log('Active discount mappings:', results);
      return results;
    }

    async ensureCollectionProducts(qualifyingCollections) {
      if (!Array.isArray(qualifyingCollections) || !qualifyingCollections.length) return;

      const cache = this.collectionProductCache || (this.collectionProductCache = {});
      const promises = [];

      qualifyingCollections.forEach((col) => {
        if (!col || !col.handle) return;
        const handle = String(col.handle);
        if (cache[handle]) return;
        promises.push(
          this.fetchCollectionProducts(handle).then((ids) => {
            cache[handle] = ids;
          }),
        );
      });

      if (promises.length) {
        await Promise.all(promises);
      }
    }

    async fetchCollectionProducts(handle) {
      try {
        const encoded = encodeURIComponent(handle);
        const res = await fetch(`/collections/${encoded}/products.json?limit=250`, {
          credentials: 'same-origin',
        });
        if (!res.ok) {
          this.log('Failed to fetch collection products for', handle, res.status);
          return [];
        }
        const data = await res.json();
        const products = Array.isArray(data.products) ? data.products : [];
        const ids = products.map((p) => String(p.id));
        this.log(`Loaded ${ids.length} products for collection`, handle);
        return ids;
      } catch (e) {
        this.log('Error fetching collection products for', handle, e);
        return [];
      }
    }

    async fetchCart() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) {
          this.log('Failed to fetch cart.js:', res.status);
          return null;
        }
        const cart = await res.json();
        return cart;
      } catch (e) {
        this.log('Error fetching cart.js:', e);
        return null;
      }
    }

    async syncGiftsWithCart(cart, activeMappings) {
      const activePairs = activeMappings || [];

      const activeMap = new Map();
      activePairs.forEach((pair) => {
        activeMap.set(pair.mapping, pair.code);
      });

      for (let i = 0; i < this.mappings.length; i += 1) {
        const mapping = this.mappings[i];
        if (!mapping || !mapping.product || !mapping.product.variantId) continue;

        const activeCode = activeMap.get(mapping) || null;
        if (!activeCode) {
          await this.ensureGiftRemoved(cart, mapping);
        } else {
          await this.ensureGiftQuantity(cart, mapping, activeCode);
        }
      }
    }

    async ensureGiftQuantity(cart, mapping, code) {
      const variantId = String(mapping.product.variantId);
      const qualifyingVariantIds = Array.isArray(mapping.qualifyingVariantIds)
        ? mapping.qualifyingVariantIds.map((id) => String(id))
        : [];
      const qualifyingProductIds = Array.isArray(mapping.qualifyingProductIds)
        ? mapping.qualifyingProductIds.map((id) => String(id))
        : [];
      const qualifyingCollections = Array.isArray(mapping.qualifyingCollections)
        ? mapping.qualifyingCollections
        : [];

      await this.ensureCollectionProducts(qualifyingCollections);

      const allQualifyingProductIds = new Set(qualifyingProductIds);

      if (qualifyingCollections.length && this.collectionProductCache) {
        qualifyingCollections.forEach((col) => {
          if (!col || !col.handle) return;
          const handle = String(col.handle);
          const ids = this.collectionProductCache[handle];
          if (!Array.isArray(ids)) return;
          ids.forEach((id) => allQualifyingProductIds.add(String(id)));
        });
      }

      const finalQualifyingProductIds = Array.from(allQualifyingProductIds);

      if (!qualifyingVariantIds.length && !finalQualifyingProductIds.length) {
        this.log(
          'Mapping has no qualifyingVariantIds/qualifyingProductIds (including collections), skipping:',
          mapping,
        );
        return;
      }

      let totalQualifyingQty = 0;
      cart.items.forEach((item) => {
        const itemVariantId = String(item.variant_id);
        const itemProductId = String(item.product_id);
        const matchesVariant = qualifyingVariantIds.indexOf(itemVariantId) !== -1;
        const matchesProduct = finalQualifyingProductIds.indexOf(itemProductId) !== -1;

        if (matchesVariant || matchesProduct) {
          totalQualifyingQty += item.quantity;
        }
      });

      const dynamicQuantity = mapping.dynamicQuantity !== false;
      const maxGiftQuantity =
        typeof mapping.maxGiftQuantity === 'number' && mapping.maxGiftQuantity > 0
          ? mapping.maxGiftQuantity
          : 9999;

      let targetGiftQty = 0;
      if (dynamicQuantity) {
        targetGiftQty = Math.min(totalQualifyingQty, maxGiftQuantity);
      } else {
        targetGiftQty = totalQualifyingQty > 0 ? 1 : 0;
      }

      this.log(
        'ensureGiftQuantity',
        'code=',
        code,
        'totalQualifyingQty=',
        totalQualifyingQty,
        'targetGiftQty=',
        targetGiftQty,
      );

      const existing = this.findExistingGiftItem(cart, variantId, code);

      if (targetGiftQty <= 0) {
        if (existing) {
          await this.changeCartLineQuantity(existing.index, 0);
        }
        return;
      }

      if (!existing) {
        await this.addGiftLine(variantId, targetGiftQty, code);
        return;
      }

      if (existing.item.quantity !== targetGiftQty) {
        await this.changeCartLineQuantity(existing.index, targetGiftQty);
      }
    }

    async ensureGiftRemoved(cart, mapping) {
      const variantId = String(mapping.product.variantId);

      for (let i = cart.items.length - 1; i >= 0; i -= 1) {
        const item = cart.items[i];
        const itemVariantId = String(item.variant_id);
        const props = item.properties || {};
        const isDiscountGift =
          props._is_discount_gift === 'true' ||
          props._is_discount_gift === true ||
          props._is_discount_gift === '1';

        if (itemVariantId === variantId && isDiscountGift) {
          const lineIndex = i + 1;
          this.log('Removing stale discount gift at line', lineIndex);
          await this.changeCartLineQuantity(lineIndex, 0);
        }
      }
    }

    findExistingGiftItem(cart, variantId, code) {
      const targetVariantId = String(variantId);
      const targetCode = String(code || '').toUpperCase();

      for (let i = 0; i < cart.items.length; i += 1) {
        const item = cart.items[i];
        const itemVariantId = String(item.variant_id);
        const props = item.properties || {};

        const isDiscountGift =
          props._is_discount_gift === 'true' ||
          props._is_discount_gift === true ||
          props._is_discount_gift === '1';

        const itemCode = (props._discount_code || '').toString().toUpperCase();

        if (itemVariantId === targetVariantId && isDiscountGift && itemCode === targetCode) {
          return { item, index: i + 1 };
        }
      }

      return null;
    }

    async addGiftLine(variantId, quantity, code) {
      try {
        this.log('Adding discount gift line', { variantId, quantity, code });

        const body = {
          items: [
            {
              id: Number(variantId),
              quantity,
              properties: {
                _is_discount_gift: 'true',
                _discount_code: String(code || ''),
                _added_at: Date.now().toString(),
              },
            },
          ],
        };

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          this.log('Failed to add discount gift line:', res.status);
          return;
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      } catch (e) {
        this.log('Error adding discount gift line:', e);
      }
    }

    async changeCartLineQuantity(lineIndex, quantity) {
      try {
        this.log('Updating cart line quantity', { lineIndex, quantity });

        const body = {
          line: lineIndex,
          quantity,
        };

        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          this.log('Failed to change cart line quantity:', res.status);
          return;
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      } catch (e) {
        this.log('Error changing cart line quantity:', e);
      }
    }
  }

  window.discountGiftManager = new DiscountGiftManager(config);
})();
